import React, { useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarAlt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useAlterarDataInventario } from 'hooks/useSngpc';
import {
    toDateInputValue,
    fromDateInputValue,
    getMaxDateForInput,
    getMinDateForInput,
    validateInventoryDate
} from 'utils/dateUtils';

interface ModalAlterarDataProps {
    show: boolean;
    onHide: () => void;
    inventario: {
        id: number;
        data: string;
    } | null;
}

const ModalAlterarData: React.FC<ModalAlterarDataProps> = ({ show, onHide, inventario }) => {
    const [dataInventario, setDataInventario] = useState('');
    const [validationError, setValidationError] = useState<string>('');
    const alterarDataMutation = useAlterarDataInventario();

    useEffect(() => {
        if (inventario) {
            // ✅ Usar utilitário para converter data do backend para input
            setDataInventario(toDateInputValue(inventario.data));
        } else {
            setDataInventario('');
        }
        setValidationError('');
    }, [inventario]);

    const handleSubmit = async () => {
        if (!inventario) return;

        // ✅ Validação no frontend usando utilitário
        const validation = validateInventoryDate(dataInventario);
        if (!validation.isValid) {
            setValidationError(validation.errorMessage || 'Data inválida');
            return;
        }

        // ✅ Converter para DateTimeOffset usando utilitário
        const dateTimeOffset = fromDateInputValue(dataInventario);

        try {
            await alterarDataMutation.mutateAsync({
                inventarioId: inventario.id,
                novaData: dateTimeOffset // Agora é DateTimeOffset/string ISO
            });
            onHide();
        } catch (error) {
            console.error('Erro ao alterar data:', error);
        }
    };

    const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newDate = e.target.value;
        setDataInventario(newDate);

        // Limpar erro anterior e validar nova data
        setValidationError('');
        if (newDate) {
            const validation = validateInventoryDate(newDate);
            if (!validation.isValid) {
                setValidationError(validation.errorMessage || 'Data inválida');
            }
        }
    };

    return (
        <Modal show={show} onHide={onHide} centered>
            <Modal.Header closeButton>
                <Modal.Title>
                    <FontAwesomeIcon icon={faCalendarAlt} className="me-2 text-primary-emphasis" />
                    Alterar Data do Inventário
                </Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <Form.Group className="mb-3">
                        <Form.Label>
                            <FontAwesomeIcon icon={faCalendarAlt} className="me-2" />
                            Nova Data do Inventário
                        </Form.Label>
                        <Form.Control
                            type="date"
                            value={dataInventario}
                            onChange={handleDateChange}
                            min={getMinDateForInput()} // ✅ Data mínima dinâmica
                            max={getMaxDateForInput()} // ✅ Data máxima dinâmica
                            isInvalid={!!validationError}
                        />
                        {validationError && (
                            <Form.Control.Feedback type="invalid">
                                {validationError}
                            </Form.Control.Feedback>
                        )}
                        <Form.Text className="text-body-secondary">
                            A data não pode ser futura nem ter mais de 7 dias de atraso.
                        </Form.Text>
                    </Form.Group>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={onHide}>
                    Cancelar
                </Button>
                <Button
                    variant="primary"
                    onClick={handleSubmit}
                    disabled={alterarDataMutation.isPending || !!validationError || !dataInventario}
                >
                    {alterarDataMutation.isPending && (
                        <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                    )}
                    Alterar Data
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ModalAlterarData; 